/* sticky-atc-panel
 * ---------------------------------------------------------------
 * Mobile-First Sticky-Bar mit echtem Add-to-Cart (AJAX).
 * - Single-Variant oder Variant gewaehlt: POST /cart/add.js -> Cart-Drawer
 * - Multi-Variant ohne Auswahl: smooth-scroll zur Variant-Picker
 * - Erfolgs/Fehler-States: Button-Text + Loading-Spinner
 * - Out-of-Stock erkennen und Button disablen
 */
if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection ? this.productSection.querySelector('.product-info') : null;
      this.productForm = this.productSection ? this.productSection.querySelector('product-form') : null;
      this.addBtn = this.querySelector('[data-sticky-atc-add]');

      if ('IntersectionObserver' in window) {
        this.bindVisibilityToggle();
      }
      if (this.addBtn) {
        this.addBtn.addEventListener('click', this.handleAdd.bind(this));
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindVisibilityToggle() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });
      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);
      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }

    /**
     * Liefert die aktuelle Variant-ID aus dem Theme-Form (auto-gepflegt
     * via product-form custom element).
     */
    getSelectedVariantId() {
      if (!this.productForm) return null;
      const idInput = this.productForm.querySelector('input[name="id"]');
      return idInput && idInput.value ? idInput.value : null;
    }

    /**
     * Scrolle smooth zur Variant-Picker (wenn keine Auswahl).
     */
    scrollToVariantPicker() {
      const anchor = document.getElementById('variants') || document.getElementById('product-info');
      if (!anchor) return;
      const headerOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-end')) || 80;
      const top = anchor.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    /**
     * Hauptverhalten: triggert das Theme-product-form (macht AJAX-Add inklusive
     * Cart-Drawer-Refresh + Sections-Update). Eigener fetch wuerde nur das
     * LineItem zurueckgeben, aber nicht die gerenderten Cart-Sections.
     */
    handleAdd(evt) {
      evt.preventDefault();

      const hasVariants = this.addBtn.dataset.hasVariants === 'true';
      const variantId = this.getSelectedVariantId();

      // Multi-Variant ohne Auswahl: Scroll-Hinweis statt blinder Add
      if (hasVariants && !variantId) {
        this.scrollToVariantPicker();
        return;
      }
      if (!variantId) return;

      // Form vom Theme triggern (handleSubmit dort macht alles richtig:
      // POST mit sections=, cartDrawer.renderContents(), Cart-Icon-Update)
      const form = this.productForm ? this.productForm.querySelector('form') : null;
      if (!form) {
        this.setBtnState('error');
        setTimeout(() => this.setBtnState('idle'), 2000);
        return;
      }

      this.setBtnState('loading');

      // Erfolg-Listener: oeffentliches Theme-Event "on:cart:add" (siehe product-form.js)
      const onAdded = () => {
        this.setBtnState('added');
        setTimeout(() => this.setBtnState('idle'), 2000);
        cleanup();
      };
      const onError = () => {
        this.setBtnState('error');
        setTimeout(() => this.setBtnState('idle'), 2500);
        cleanup();
      };
      const cleanup = () => {
        document.removeEventListener('on:cart:add', onAdded);
        document.removeEventListener('on:line-item:change', onAdded);
        document.removeEventListener('on:cart:error', onError);
      };
      document.addEventListener('on:cart:add', onAdded, { once: true });
      document.addEventListener('on:line-item:change', onAdded, { once: true });
      document.addEventListener('on:cart:error', onError, { once: true });

      // Fallback: Falls keine Events kommen, reset nach 5s
      setTimeout(() => {
        if (this.addBtn.dataset.state === 'loading') {
          this.setBtnState('idle');
          cleanup();
        }
      }, 5000);

      // Theme-Form submitten -- macht alles richtig
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }

    setBtnState(state) {
      if (!this.addBtn) return;
      this.addBtn.dataset.state = state;
      this.addBtn.disabled = (state === 'loading');
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
